/**
 * Client Tickets Page
 * View and manage support tickets
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  LifeBuoy,
  Plus,
  Clock,
  CheckCircle,
  XCircle,
  MessageCircle,
  AlertCircle,
  RefreshCw,
  Search,
  Filter,
} from 'lucide-react';

interface Ticket {
  id: number;
  subject: string;
  status: 'open' | 'answered' | 'customer-reply' | 'on-hold' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  departmentName: string;
  lastReply: string;
  createdAt: string;
  replyCount: number;
  unreadReplies: number;
}

const statusConfig = {
  open: {
    label: 'Open',
    icon: Clock,
    color: 'text-orange-400',
    bgColor: 'bg-orange-900/20',
    borderColor: 'border-orange-700',
  },
  answered: {
    label: 'Answered',
    icon: MessageCircle,
    color: 'text-green-400',
    bgColor: 'bg-green-900/20',
    borderColor: 'border-green-700',
  },
  'customer-reply': {
    label: 'Awaiting Reply',
    icon: AlertCircle,
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-900/20',
    borderColor: 'border-yellow-700',
  },
  'on-hold': {
    label: 'On Hold',
    icon: Clock,
    color: 'text-orange-400',
    bgColor: 'bg-orange-900/20',
    borderColor: 'border-orange-700',
  },
  closed: {
    label: 'Closed',
    icon: CheckCircle,
    color: 'text-gray-400',
    bgColor: 'bg-gray-900/20',
    borderColor: 'border-gray-700',
  },
};

const priorityConfig = {
  low: { label: 'Low', color: 'text-gray-400' },
  medium: { label: 'Medium', color: 'text-cyan-400' },
  high: { label: 'High', color: 'text-orange-400' },
  urgent: { label: 'Urgent', color: 'text-red-400' },
};

const TicketsPage: React.FC = () => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('active');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/client/tickets', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setTickets(data.data.tickets);
      }
    } catch (error) {
      console.error('Failed to load tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const activeStatuses = ['open', 'answered', 'customer-reply', 'on-hold'];

  const filteredTickets = tickets.filter((ticket) => {
    // Filter by status
    const statusMatch =
      filterStatus === 'all' ||
      (filterStatus === 'active' && activeStatuses.includes(ticket.status)) ||
      (filterStatus === 'closed' && ticket.status === 'closed') ||
      ticket.status === filterStatus;

    // Filter by search
    const searchMatch =
      searchQuery === '' ||
      ticket.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.id.toString().includes(searchQuery);

    return statusMatch && searchMatch;
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }
  };

  const openTickets = tickets.filter((t) => activeStatuses.includes(t.status)).length;
  const unreadCount = tickets.reduce((sum, t) => sum + (t.unreadReplies || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <LifeBuoy className="w-8 h-8" />
            Support Tickets
          </h1>
          <p className="text-gray-400 mt-2">Get help from our support team</p>
        </div>
        <Link
          to="/client/tickets/new"
          className="px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all duration-300 flex items-center gap-2 font-medium shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40"
        >
          <Plus className="w-5 h-5" />
          New Ticket
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 animate-fade-in">
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-4 border border-slate-700/50 hover:border-orange-500/30 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Total Tickets</p>
              <p className="text-2xl font-bold text-white mt-1">{tickets.length}</p>
            </div>
            <div className="w-10 h-10 bg-orange-500/10 rounded-lg flex items-center justify-center">
              <LifeBuoy className="w-5 h-5 text-orange-400" />
            </div>
          </div>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-4 border border-slate-700/50 hover:border-green-500/30 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Open</p>
              <p className="text-2xl font-bold text-green-400 mt-1">{openTickets}</p>
            </div>
            <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-green-400" />
            </div>
          </div>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-4 border border-slate-700/50 hover:border-yellow-500/30 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Unread Replies</p>
              <p className="text-2xl font-bold text-yellow-400 mt-1">{unreadCount}</p>
            </div>
            <div className="w-10 h-10 bg-yellow-500/10 rounded-lg flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-yellow-400" />
            </div>
          </div>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-4 border border-slate-700/50 hover:border-slate-600/50 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Closed</p>
              <p className="text-2xl font-bold text-slate-400 mt-1">
                {tickets.filter((t) => t.status === 'closed').length}
              </p>
            </div>
            <div className="w-10 h-10 bg-slate-500/10 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-slate-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search tickets by subject or ID..."
            className="w-full pl-10 pr-4 py-3 bg-slate-800/80 border border-slate-700/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-colors"
          />
        </div>

        <div className="flex gap-2">
          {['active', 'all', 'open', 'answered', 'closed'].map((status) => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-4 py-3 rounded-lg font-medium transition-all duration-300 whitespace-nowrap ${
                filterStatus === status
                  ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/25'
                  : 'bg-slate-800/80 text-slate-400 hover:bg-slate-700/50 hover:text-white border border-slate-700/50'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Tickets List */}
      {loading ? (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-12 border border-slate-700/50">
          <div className="flex items-center justify-center">
            <RefreshCw className="w-8 h-8 text-orange-400 animate-spin" />
            <span className="ml-3 text-slate-400">Loading tickets...</span>
          </div>
        </div>
      ) : filteredTickets.length === 0 ? (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-12 border border-slate-700/50 text-center">
          <LifeBuoy className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-white mb-2">No Tickets Found</h3>
          <p className="text-slate-400 mb-6">
            {searchQuery
              ? 'No tickets match your search.'
              : "You don't have any support tickets yet."}
          </p>
          <Link
            to="/client/tickets/new"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all duration-300 shadow-lg shadow-orange-500/25"
          >
            <Plus className="w-5 h-5" />
            Create Your First Ticket
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredTickets.map((ticket) => {
            const statusInfo = statusConfig[ticket.status];
            const priorityInfo = priorityConfig[ticket.priority];
            const StatusIcon = statusInfo.icon;

            return (
              <Link
                key={ticket.id}
                to={`/client/tickets/${ticket.id}`}
                className={`block bg-slate-800/80 backdrop-blur-sm rounded-xl border ${statusInfo.borderColor} hover:border-orange-500/50 transition-all duration-300 p-6`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <span className="text-gray-400 text-sm font-mono">#{ticket.id}</span>
                      <span
                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${statusInfo.bgColor} ${statusInfo.color}`}
                      >
                        <StatusIcon className="w-4 h-4" />
                        {statusInfo.label}
                      </span>
                      <span className={`text-sm font-medium ${priorityInfo.color}`}>
                        {priorityInfo.label} Priority
                      </span>
                      {ticket.unreadReplies > 0 && (
                        <span className="px-2 py-1 bg-orange-500 text-white rounded-full text-xs font-medium">
                          {ticket.unreadReplies} new
                        </span>
                      )}
                    </div>

                    <h3 className="text-lg font-semibold text-white mb-2">{ticket.subject}</h3>

                    <div className="flex items-center gap-4 text-sm text-gray-400">
                      <span className="flex items-center gap-1">
                        <LifeBuoy className="w-4 h-4" />
                        {ticket.departmentName}
                      </span>
                      <span className="flex items-center gap-1">
                        <MessageCircle className="w-4 h-4" />
                        {ticket.replyCount} {ticket.replyCount === 1 ? 'reply' : 'replies'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        Last updated {formatDate(ticket.lastReply)}
                      </span>
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="text-gray-400 text-sm">
                      Created {formatDate(ticket.createdAt)}
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TicketsPage;
